// The night as a whole: pools in order, one shared tide budget, totals.
import { nightPlan, buildPool } from './night.js';
import { MODE_TUNE } from './content.js';
import { resolveTurn, poolDone, litCells, openCells, stencilProgress } from './pool.js';
const stencilPermille = p => stencilProgress(p).permille;

// `attempts` (optional) is the verified layout index per pool from the baked table.
export function startNight(iso, attempts = [], { shells = null, ward = false, okite = null, mode = null, lanterns = [] } = {}) {
  const plan = nightPlan(iso, { shells, ward, okite, mode, lanterns });
  const pools = plan.basins.map((_, i) => buildPool(plan, i, attempts[i] || 0));
  return {
    plan, attempts, index: 0,
    pool: pools[0],
    tideLeft: plan.rules.turns + (MODE_TUNE[plan.mode]?.turns || 0),
    turnsUsed: 0,
    tideMax: plan.rules.turns + (MODE_TUNE[plan.mode]?.turns || 0),
    totalLittles: pools.reduce((n, p) => n + p.littles.length, 0),
    history: [],            // per finished pool: { basin, saved, taken, lit, open, pens, allFour, bestPen, turns }
    over: false, reason: null,
  };
}

const poolSummary = (p, turns) => ({
  basin: p.basin, saved: p.stats.saved, taken: p.stats.taken, total: p.littles.length,
  lit: litCells(p.grid), open: openCells(p.grid), pens: p.stats.pens, allFour: p.stats.allFour,
  bestPen: p.stats.bestPen, turns, calm: p.boss ? p.boss.calm : null, pearls: p.stats.pearls,
  glass: glassOf(p),
  mode: p.mode, motif: p.stencil ? true : undefined,
  picture: p.stencil ? stencilPermille(p) : undefined,
  sent: p.littles.filter(l => l.kind === 'lantern' && l.state === 'saved').map(l => l.name),
  stars: p.littles.filter(l => l.kind === 'star').map(l => ({ x: l.x, y: l.y, lit: l.state === 'saved' })),
  savedKinds: p.littles.filter(l => l.state === 'saved').reduce((m, l) => ((m[l.kind] = (m[l.kind] || 0) + 1), m), {}),
});

// Compact record of the lit cells and who closed them, for the stained-glass panel.
function glassOf(p) {
  const g = p.grid;
  let rle = [], last = -1, n = 0;
  for (let i = 0; i < g.kind.length; i++) {
    const v = g.kind[i] === 3 ? 1 + g.pen[i] : g.kind[i] === 0 ? 0 : g.dark[i] ? 18 : 19; // 0 out, 1..17 lit+pen, 18 dark, 19 water
    if (v === last) n++; else { if (n) rle.push(last, n); last = v; n = 1; }
  }
  rle.push(last, n);
  // Which gel each lit cell is (1 + the crab that closed it, 6 the moon, 0 rocks).
  const gel = [];
  last = -1; n = 0;
  for (let i = 0; i < g.kind.length; i++) {
    const v = g.kind[i] === 3 ? (g.pen[i] === 16 ? 6 : g.penBy[i]) : 0;
    if (v === last) n++; else { if (n) gel.push(last, n); last = v; n = 1; }
  }
  gel.push(last, n);
  // Picture cells as a sorted index list, stars with whether they were scooped.
  return {
    basin: p.basin, w: g.w, h: g.h, rle, gel, mode: p.mode,
    st: p.stencil ? p.stencil.cells : null,
    stars: p.littles.filter(l => l.kind === 'star').map(l => [Math.floor((l.x - g.x0) / 1024), Math.floor((l.y - g.y0) / 1024), l.state === 'saved' ? 1 : 0]),
    colors: p.crabs.map(c => c.shell),
  };
}

// Play one turn. Returns the turn result (for playback) and the next run state.
export function playTurn(run, inputs) {
  const res = resolveTurn(run.pool, inputs);
  const next = { ...run, pool: res.pool, tideLeft: run.tideLeft - 1, turnsUsed: run.turnsUsed + 1 };
  next.poolTurns = (run.poolTurns || 0) + 1;
  if (poolDone(res.pool)) {
    next.history = [...run.history, poolSummary(res.pool, next.poolTurns)];
    next.poolCleared = true;
  } else if (next.tideLeft <= 0) {
    // The tide comes back: everyone still out in the pools is swept away.
    next.history = [...run.history, poolSummary(res.pool, next.poolTurns)];
    next.over = true; next.reason = 'tide';
  }
  if (next.poolCleared && next.index + 1 >= run.plan.basins.length) { next.over = true; next.reason = 'dawn'; }
  return { res, run: next };
}

// Move to the next pool after a clear.
export function nextPool(run) {
  const index = run.index + 1;
  return { ...run, index, pool: buildPool(run.plan, index, run.attempts[index] || 0), poolCleared: false, poolTurns: 0 };
}

export function nightTotals(run) {
  const h = run.history;
  const saved = h.reduce((n, p) => n + p.saved, 0);
  return {
    saved, total: run.totalLittles,
    pools: h.length, poolsPlanned: run.plan.basins.length,
    tideLeft: Math.max(0, run.tideLeft), turnsUsed: run.turnsUsed,
    pens: h.reduce((n, p) => n + p.pens, 0),
    allFour: h.reduce((n, p) => n + p.allFour, 0),
    bestPen: h.reduce((n, p) => Math.max(n, p.bestPen), 0),
    calm: h.some(p => p.calm === true),
    pearls: h.reduce((n, p) => n + (p.pearls || 0), 0),
    picture: h.reduce((m, p) => Math.max(m, p.picture ?? -1), -1),   // best picture permille, -1 if none
  };
}
